import { useRouter } from 'next/router';
import { RegistrationField } from '../interfaces/interfaces';
import { setEventRegistration } from '../reduxToolkit/slices/commonSlice';
import { useSelector, useDispatch } from '../reduxToolkit/typedRedux';
import { seeIncompleteProfileModalService } from '../services/services';
import { useLanguage } from './useLanguage';
import { usePermission } from './usePermission';
import { store } from '../reduxToolkit/store';

const useRegistration = () => {
  const event = useSelector(({ common }) => common.event);
  const registration = useSelector(({ common }) => common.eventRegistration);
  const registrationFields = useSelector((state) => state.common.registrationFields);

  const dispatch = useDispatch();
  const { isEventHost } = usePermission();
  const router = useRouter();
  const { supportedLanguages } = useLanguage();

  const filterRegistrationFieldsForIncompleteProfile = (field: RegistrationField) => {
    const incomplete = !registration?.registrationFields.find((regField) => regField.field._id === field._id && regField.value);
    const notHidden = !field.isHidden;
    const notEmail = !field.isEmailField;
    const showInUserProfile = field.showInUserProfile;
    const isEditable = field.canEditInProfile;

    return incomplete && notHidden && notEmail && showInUserProfile && isEditable;
  };

  const seeIncompleteProfileModal = async () => {
    if (!registration) return;
    try {
      await seeIncompleteProfileModalService(registration._id);
      dispatch(setEventRegistration({ ...store.getState().common.eventRegistration, hasSeenIncompleteProfileModal: true }));
    } catch (error) {
      console.error(error);
    }
  };

  function isCurrentFieldVisible(currentField: RegistrationField, fields: RegistrationField[], watch: any) {
    if (!currentField.parentField?.field) return true;

    const parentField = fields.find((f) => f._id === currentField?.parentField?.field);
    if (!parentField) return;

    const expectedParentFieldValue = currentField.parentField?.value;
    const currentParentFieldValue = watch(currentField.parentField?.field);

    const parentFieldValueMatchesCurrentFieldExpected = currentParentFieldValue?.split(',').find((v: string) => {
      return v === expectedParentFieldValue;
    });

    if (!parentFieldValueMatchesCurrentFieldExpected) return;

    const grandParentField = parentField.parentField;

    return (
      !grandParentField?.field ||
      grandParentField?.value === watch(grandParentField?.field) ||
      watch(grandParentField?.field)
        ?.split(',')
        .find((v: string) => {
          return v === grandParentField?.value;
        })
    );
  }

  const isLiveEvent = router.pathname?.includes('/live');
  const openIncompleteProfileModal =
    !isEventHost() &&
    event?.settings?.incompleteProfile?.active === true &&
    registration?.hasSeenIncompleteProfileModal === false &&
    registrationFields.filter(filterRegistrationFieldsForIncompleteProfile).length > 0 &&
    isLiveEvent;

  function isValidLanguage(languageField: Partial<RegistrationField>) {
    if (!languageField.value) return false;
    return supportedLanguages.includes(languageField.value) || !languageField.value;
  }

  function isEventVystemEmail(email: string): boolean {
    return email.includes('event.vystem.io');
  }

  function isEventImportEmail(email: string): boolean {
    return email.includes('import') && email.includes('vystem.io');
  }

  return {
    openIncompleteProfileModal,
    filterRegistrationFieldsForIncompleteProfile,
    seeIncompleteProfileModal,
    isCurrentFieldVisible,
    isValidLanguage,
    isEventVystemEmail,
    isEventImportEmail,
  };
};
export default useRegistration;
